/**
 * useNotebookUrlLoader (Intelligence Studio)
 *
 * SRP hook: load notebook from URL param and hydrate local UI state.
 * Extracted (copy/paste) from containers/IntelligenceStudioPage.jsx.
 *
 * ✅ ENTERPRISE: Now includes timeout handling, stuck detection, and auto-recovery.
 */

import { useEffect, useRef, useCallback } from 'react';
import { traceFetch } from '@/pages/intelligence-studio/utils/traceContext';
import { resolveAuthToken } from '@/pages/intelligence-studio/utils/authTokens';
import { applyVaultStateToNotebook } from '@/pages/intelligence-studio/utils/vaultState';
import { getApiBaseUrl } from '../../../config/runtimeUrls';

// ✅ ENTERPRISE: Constants for timeout and stuck detection
const LOADING_TIMEOUT_MS = 15000; // 15 seconds max wait
const STUCK_DETECTION_THRESHOLD_MS = 60 * 1000; // 1 minute in processing = likely stuck
const MAX_RETRY_ATTEMPTS = 3;
const STATE_RESOLVER_ENABLED = process.env.REACT_APP_IS_STATE_RESOLVER !== 'false';

export default function useNotebookUrlLoader({
  notebookIdFromUrl,
  selectedNotebook,
  setSelectedNotebook,
  setView,
  setNotebooks,
  setSearchParams,
  intelligenceStudioApi,
  convertFilesToSmartTableRows,
  setNanoMetadataRowsDirectly,
}) {
  const loadAttemptRef = useRef(0);
  const abortControllerRef = useRef(null);

  const applyAnalysisValidation = useCallback(
    async notebook => {
      try {
        if (!notebook?.id || !Array.isArray(notebook.files)) return notebook;
        if (!intelligenceStudioApi?.validateAnalysisFiles) return notebook;
        const fileIds = notebook.files
          .map(f => f?.fileId || f?.id)
          .filter(Boolean)
          .map(String);
        if (fileIds.length === 0) return notebook;

        const response = await intelligenceStudioApi.validateAnalysisFiles(notebook.id, fileIds);
        const payload = response?.data?.data || response?.data || response;
        const missing = Array.isArray(payload?.missingFileIds) ? payload.missingFileIds : [];
        if (!missing.length) return notebook;
        const missingSet = new Set(missing.map(String));

        const updatedFiles = notebook.files.map(f => {
          const fid = String(f?.fileId || f?.id || '');
          if (!fid || !missingSet.has(fid)) return f;
          const status = String(f?.lifecycleSnapshot?.status || f?.status || '').toLowerCase();
          const isReady =
            f?.isReady === true || status === 'ready' || status === 'ready_with_warnings';
          // Do not mark READY files as ghost from analysis validation mismatches.
          if (isReady) return f;
          return {
            ...f,
            status: 'ghost',
            error: 'Upload incomplete',
            processingStage: 'ghost',
            processingMessage: 'Upload incomplete',
            ghostStatus: 'DETECTED',
            ghostReason: 'Upload incomplete',
          };
        });

        return { ...notebook, files: updatedFiles };
      } catch (err) {
        console.warn('⚠️ [IntelligenceStudio] Analysis validation failed:', err?.message || err);
        return notebook;
      }
    },
    [intelligenceStudioApi]
  );

  // ✅ ENTERPRISE: Auto-finalize stuck notebooks
  const tryAutoFinalize = useCallback(async notebookId => {
    try {
      console.log(`🔧 [IntelligenceStudio] Attempting to auto-finalize stuck notebook: ${notebookId}`);
      // Call the finalize endpoint to fix stuck notebooks
      const token = resolveAuthToken();
      const apiBase = String(getApiBaseUrl() || '').replace(/\/+$/, '');
      const response = await traceFetch(
        `${apiBase}/intelligence-studio/notebooks/${notebookId}/finalize`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
        {
          apiName: 'notebook.finalize',
          pipelineStage: 'http',
          notebookId,
        }
      );
      const result = await response.json();
      if (result?.finalized) {
        console.log(`✅ [IntelligenceStudio] Successfully finalized stuck notebook: ${notebookId}`);
        return true;
      }
    } catch (err) {
      console.warn(`⚠️ [IntelligenceStudio] Auto-finalize failed:`, err.message);
    }
    return false;
  }, []);

  // ✅ FIX: Load notebook from URL parameter ALWAYS when URL has an ID
  // This ensures files appear when navigating to vault page
  useEffect(() => {
    const loadNotebookFromUrl = async () => {
      if (!notebookIdFromUrl) return;

      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // ✅ Only load if:
      // 1. No notebook selected yet, OR
      // 2. Different notebook ID than current, OR
      // 3. Current notebook has no files but should have (force refresh)
      const needsLoad =
        !selectedNotebook ||
        selectedNotebook.id !== notebookIdFromUrl ||
        (selectedNotebook.id === notebookIdFromUrl && !selectedNotebook.files?.length);

      if (!needsLoad) {
        console.log(
          `📂 [IntelligenceStudio] Notebook ${notebookIdFromUrl} already loaded with ${selectedNotebook?.files?.length || 0} files`
        );
        return;
      }

      try {
        console.log(`📂 [IntelligenceStudio] Loading notebook from URL: ${notebookIdFromUrl}`);
        loadAttemptRef.current += 1;
        const currentAttempt = loadAttemptRef.current;

        // ✅ ENTERPRISE: Add timeout to prevent infinite loading
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('LOADING_TIMEOUT')), LOADING_TIMEOUT_MS)
        );

        const response = await Promise.race([
          intelligenceStudioApi.getNotebook(notebookIdFromUrl),
          timeoutPromise,
        ]);

        // ✅ FIX: apiClient returns {success, data} - notebook is in response.data
        // Handle both direct data and nested response structures
        let notebook = null;
        if (response?.data?.id) {
          notebook = response.data; // {success, data: {id, files...}} → data is notebook
        } else if (response?.id) {
          notebook = response; // Direct notebook object
        } else if (response?.data?.data?.id) {
          notebook = response.data.data; // Double nested (unlikely)
        }

        console.log(`📂 [IntelligenceStudio] Parsed notebook:`, {
          id: notebook?.id,
          title: notebook?.title,
          fileCount: notebook?.files?.length || 0,
        });

        if (notebook) {
          // ✅ Ensure files array exists
          notebook.files = notebook.files || [];

          // ✅ Phase D: Attach canonical vault state (feature-flagged)
          let resolvedStateData = null;
          if (STATE_RESOLVER_ENABLED) {
            try {
              const stateResponse = await intelligenceStudioApi.getNotebookState(notebookIdFromUrl);
              const stateData =
                stateResponse?.data?.vaultState
                  ? stateResponse.data
                  : stateResponse?.data?.data?.vaultState
                    ? stateResponse.data.data
                    : stateResponse?.vaultState
                      ? stateResponse
                      : null;
              resolvedStateData = stateData || null;
              if (resolvedStateData) {
                notebook = applyVaultStateToNotebook(notebook, resolvedStateData);
              }
            } catch (stateErr) {
              console.warn('⚠️ [IntelligenceStudio] Vault state load failed:', stateErr?.message || stateErr);
            }
          }

          console.log(
            `✅ [IntelligenceStudio] Loaded: ${notebook.title} with ${notebook.files.length} files`
          );

          // ✅ ENTERPRISE: Detect stuck notebooks (processing for too long)
          const isStuck = (() => {
            const vaultState = notebook?._vaultState?.vaultState || null;
            if (STATE_RESOLVER_ENABLED ? vaultState !== 'INGESTING' : notebook.status !== 'processing') return false;
            const updatedAt = new Date(notebook.updatedAt || notebook.createdAt);
            const ageMs = Date.now() - updatedAt.getTime();
            return ageMs > STUCK_DETECTION_THRESHOLD_MS;
          })();

          if (isStuck) {
            console.warn(`⚠️ [IntelligenceStudio] Notebook ${notebook.id} appears stuck in processing`);
            // Attempt auto-finalize
            const finalized = await tryAutoFinalize(notebook.id);
            if (finalized) {
              // Reload the notebook after finalization
              const refreshed = await intelligenceStudioApi.getNotebook(notebookIdFromUrl);
              const refreshedNb = refreshed?.data?.id
                ? refreshed.data
                : refreshed?.id
                  ? refreshed
                  : null;
              if (refreshedNb) {
                notebook = { ...refreshedNb, files: refreshedNb.files || [] };
                if (resolvedStateData) {
                  notebook = applyVaultStateToNotebook(notebook, resolvedStateData);
                }
                console.log(
                  `✅ [IntelligenceStudio] Refreshed after auto-finalize: status=${notebook.status}`
                );
              }
            } else {
              // Mark as stuck for UI to handle
              notebook._isStuck = true;
              notebook._stuckMessage =
                'This vault appears stuck in processing. Use the "Fix Stuck Vault" button to resolve.';
            }
          }

          notebook = await applyAnalysisValidation(notebook);

          // ✅ ENTERPRISE: Server is authoritative on reload (avoid resurrecting ghost rows)
          setSelectedNotebook(() => ({
            ...notebook,
            files: notebook.files || [],
          }));

          // ✅ ENTERPRISE FIX: Populate SmartTable for existing files
          // Convert existing files to SmartTable format so table appears immediately
          setSelectedNotebook(current => {
            if (current?.files?.length > 0 && setNanoMetadataRowsDirectly) {
              const smartTableRows = convertFilesToSmartTableRows(current.files);
              console.log(
                `🔬 [NanoMetadata] Converting ${current.files.length} existing files to SmartTable format`
              );

              // Set rows directly - this will make SmartTable appear instantly
              setNanoMetadataRowsDirectly(smartTableRows);
              console.log(
                `✅ [NanoMetadata] SmartTable populated with ${smartTableRows.length} rows`
              );
            }
            return current;
          });

          setView('notebook');

          // Also update notebooks list
          setNotebooks(prev => {
            const exists = prev.some(n => n.id === notebook.id);
            if (exists) {
              return prev.map(n => (n.id === notebook.id ? notebook : n));
            }
            return [notebook, ...prev];
          });
        }
      } catch (err) {
        console.error('Failed to load notebook from URL:', err);

        // ✅ ENTERPRISE: Handle different error types
        if (err.message === 'LOADING_TIMEOUT') {
          console.warn(`⏱️ [IntelligenceStudio] Loading timeout for ${notebookIdFromUrl}`);

          // Try auto-finalize for stuck notebooks
          if (loadAttemptRef.current <= MAX_RETRY_ATTEMPTS) {
            const finalized = await tryAutoFinalize(notebookIdFromUrl);
            if (finalized) {
              // Retry loading after finalize
              console.log(`🔄 [IntelligenceStudio] Retrying load after auto-finalize`);
              loadNotebookFromUrl();
              return;
            }
          }

          // If all retries exhausted, show error state but keep on page
          setSelectedNotebook({
            id: notebookIdFromUrl,
            title: 'Loading Failed',
            files: [],
            status: 'error',
            isReady: false,
            _loadError: 'timeout',
            _errorMessage:
              'Loading timed out. This vault may be stuck in processing. Try refreshing or contact support.',
          });
          setView('notebook');
          return;
        }

        // Check if it's a 404 (notebook not found)
        if (err.response?.status === 404 || err.message?.includes('not found')) {
          console.warn(`🚫 [IntelligenceStudio] Notebook ${notebookIdFromUrl} not found`);
          setSelectedNotebook({
            id: notebookIdFromUrl,
            title: 'Vault Not Found',
            files: [],
            status: 'not_found',
            isReady: false,
            _loadError: 'not_found',
            _errorMessage: `Vault "${notebookIdFromUrl}" does not exist or has been deleted.`,
          });
          setView('notebook');
          return;
        }

        // Keep URL to allow retry on transient errors (auth/network)
        setSelectedNotebook(prev => ({
          ...(prev && prev.id === notebookIdFromUrl ? prev : { id: notebookIdFromUrl }),
          title: prev?.title || 'Vault Unavailable',
          files: prev?.files || [],
          status: 'error',
          isReady: false,
          _loadError: 'fetch_failed',
          _errorMessage:
            'Unable to load this vault right now. Please refresh or try again shortly.',
        }));
        setView('notebook');
        return;
      }
    };

    loadNotebookFromUrl();

    // Cleanup
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [notebookIdFromUrl, tryAutoFinalize, applyAnalysisValidation]);
}
